import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useForgotPassword } from '../api/auth';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const mutation = useForgotPassword();

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutation.mutate(email);
  };

  return (
    <div className="login-bg min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="bg-gray-900/80 backdrop-blur-xl border border-gray-700/50 rounded-2xl shadow-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 mb-4 shadow-lg shadow-amber-500/20">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Mot de passe oublie</h1>
            <p className="text-gray-400 text-sm mt-1">
              Entrez votre adresse email pour recevoir un lien de reinitialisation.
            </p>
          </div>

          {/* Success message */}
          {mutation.isSuccess && (
            <div className="mb-6 p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-green-400 text-sm text-center">
              Un email de reinitialisation a ete envoye a <strong>{email}</strong>. Verifiez votre boite de reception.
            </div>
          )}

          {/* Error message */}
          {mutation.isError && (
            <div className="mb-6 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm text-center">
              {(mutation.error as any)?.response?.data?.detail ||
                'Une erreur est survenue. Veuillez reessayer.'}
            </div>
          )}

          {/* Form */}
          {!mutation.isSuccess ? (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1.5">
                  Adresse email
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="vous@exemple.com"
                  className="w-full px-4 py-2.5 rounded-lg bg-gray-800/60 border border-gray-600/50 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/50 transition-colors"
                />
              </div>

              <button
                type="submit"
                disabled={mutation.isPending}
                className="w-full py-2.5 rounded-lg bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold hover:from-amber-600 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-amber-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2"
              >
                {mutation.isPending ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Envoi...
                  </>
                ) : (
                  'Reinitialiser'
                )}
              </button>
            </form>
          ) : (
            <button
              onClick={() => mutation.reset()}
              className="w-full py-2.5 rounded-lg bg-gray-700/50 border border-gray-600/50 text-gray-300 font-medium hover:bg-gray-700 transition-colors"
            >
              Envoyer a nouveau
            </button>
          )}

          {/* Back to login */}
          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm text-amber-400 hover:text-amber-300 transition-colors inline-flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Retour a la connexion
            </Link>
          </div>
        </div>
      </div>

      {/* Same animated gradient as login */}
      <style>{`
        .login-bg {
          background: #0a0a0f;
          position: relative;
          overflow: hidden;
        }
        .login-bg::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(ellipse at 20% 50%, rgba(245, 158, 11, 0.08) 0%, transparent 50%),
                      radial-gradient(ellipse at 80% 20%, rgba(234, 88, 12, 0.06) 0%, transparent 50%),
                      radial-gradient(ellipse at 50% 80%, rgba(168, 85, 247, 0.04) 0%, transparent 50%);
          animation: loginGradient 15s ease-in-out infinite alternate;
          pointer-events: none;
        }
        @keyframes loginGradient {
          0% { transform: translate(0%, 0%) rotate(0deg); }
          33% { transform: translate(2%, -1%) rotate(1deg); }
          66% { transform: translate(-1%, 2%) rotate(-1deg); }
          100% { transform: translate(1%, -2%) rotate(0.5deg); }
        }
      `}</style>
    </div>
  );
}
